import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiClientError } from '@/services/apiClient';
import { sportsApi } from '@/services/sportsApi';
import type { SportInfo, SportsEvent } from '@/types/sports';
import type { RootState } from '@/store';

export type SportsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface SportsState {
  sports: SportInfo[];
  events: SportsEvent[];
  byId: Record<string, SportsEvent>;
  status: SportsStatus;
  lastFetchedAt: number | null;
  error: string | null;
}

function describeError(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

const initialState: SportsState = {
  sports: [],
  events: [],
  byId: {},
  status: 'idle',
  lastFetchedAt: null,
  error: null,
};

export const fetchSports = createAsyncThunk<SportInfo[], void, { rejectValue: string }>(
  'sports/fetchSports',
  async (_arg, { rejectWithValue }) => {
    try {
      const { sports } = await sportsApi.listSports();
      return sports;
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const fetchSportsEvents = createAsyncThunk<SportsEvent[], void, { rejectValue: string }>(
  'sports/fetchEvents',
  async (_arg, { rejectWithValue }) => {
    try {
      const { events } = await sportsApi.listEvents();
      return events;
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const fetchSportsEvent = createAsyncThunk<SportsEvent, string, { rejectValue: string }>(
  'sports/fetchEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      const { event } = await sportsApi.getEvent(eventId);
      return event;
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

const sportsSlice = createSlice({
  name: 'sports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSports.fulfilled, (state, action) => {
        state.sports = action.payload;
      })
      .addCase(fetchSportsEvents.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSportsEvents.fulfilled, (state, action) => {
        state.status = 'ready';
        state.events = action.payload;
        state.lastFetchedAt = Date.now();
        // Refresh byId for hover prefetch / event card lookups.
        const next: Record<string, SportsEvent> = {};
        for (const ev of action.payload) next[ev.id] = ev;
        state.byId = next;
      })
      .addCase(fetchSportsEvents.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? action.error.message ?? 'Failed to load sports events';
      })
      .addCase(fetchSportsEvent.fulfilled, (state, action) => {
        state.byId[action.payload.id] = action.payload;
      });
  },
});

export default sportsSlice.reducer;

export function selectSportsEvents(state: RootState): SportsEvent[] {
  return state.sports.events;
}

export function selectSportsEventById(state: RootState, eventId: string): SportsEvent | undefined {
  return state.sports.byId[eventId];
}

export function selectSportsList(state: RootState): SportInfo[] {
  return state.sports.sports;
}

export function selectSportsStatus(state: RootState): SportsStatus {
  return state.sports.status;
}
